import { ResizeObserver } from "resize-observer";
import { find, isString, isNumber, isPlainObject, transform, isEqual, isObject } from "lodash";
import { encode } from "js-base64";


/**
 * This function can be used for handling
 * of cases when we want to have alternative
 * of nested undefined object properties.
 * Example of usage:
 *  {getOrElse( () => occupationDetail.jobTitles[0].name, '')}
 * In this case we will not need to check if
 * jobTitles property is defined and if it is array
 * and if there is an object with name property as a
 * first item inside that array.
 *
 * @param {function} func Function which returns the value
 * @param {*} fallbackValue
 */
export const getOrElse = (func, fallbackValue) => {
	try {
		const value = func();
		return value === null || value === undefined ? fallbackValue : value;
	} catch (e) {
		return fallbackValue;
	}
};

/**
 * Format a number money format (i.e. 987654321 --> £9,876,532.10)
 *
 * @param {Number} amount
 * @param {Number} decimalCount
 * @param {String} decimal
 * @param {String} thousands
 */
export const formatMoney = (amount, decimalCount = 2, decimal = ".", thousands = ",") => {
	try {
		if (isNaN(amount)) {
			throw Error("Input is not a numeric value");
		}

		decimalCount = Math.abs(decimalCount);
		decimalCount = isNaN(decimalCount) ? 2 : decimalCount;

		const negativeSign = amount < 0 ? "-" : "";

		let i = parseInt((amount = Math.abs(Number(amount) || 0).toFixed(decimalCount))).toString();
		let j = i.length > 3 ? i.length % 3 : 0;

		return (
			negativeSign +
			(j ? i.substr(0, j) + thousands : "") +
			i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousands) +
			(decimalCount
				? decimal +
				Math.abs(amount - i)
				.toFixed(decimalCount)
				.slice(2)
				: "")
		);
	} catch (e) {
		throw e;
	}
};

/**
 * Get the index of an object from an array of objects
 *
 * @param {Object} object
 * @param {Array} arrayOfObjects
 */
export const indexOfObject = (object, arrayOfObjects) => {
	return arrayOfObjects.findIndex((cmpObj) => objectIdenticals(cmpObj, object, true));
};

/**
 * Check if two objects are identical via JSON encoding
 * Requires both objects' structures to be identical, it
 * will compare values only
 *
 * @param {object} a
 * @param {object} b
 * @param {boolean} strict
 * @return {boolean}
 */
export const objectIdenticals = (a, b, strict = false) => {
	if (strict) {
		let deepEqual = require("deep-equal");
		return deepEqual(a, b);
	}
	return JSON.stringify(a) === JSON.stringify(b);
};

/**
 * Given an array of objects, this will find those
 * distinct by a object property
 *
 * @param {Array} array Array of objects
 * @param {String} key
 */
export const distinctBy = (array, key) => {
	let foundKeyVals = [];

	return array.filter((item) => {
		const keyVal = item[key];

		if (keyVal) {
			if (foundKeyVals.indexOf(keyVal) === -1) {
				foundKeyVals.push(keyVal);
				return true;
			}
		}

		return false;
	});
};

/**
 * Truncates a string to a length and trims at the end of
 * whole words (if character limit would place cursor in the
 * middle of a whole word, it is removed and we use the
 * preceding whole word). We also remove punctuation at the end.
 *
 * @param {String} string
 * @param {Number} length
 * @param {String} elipsis
 */
export const truncateText = (string, length = 120, elipsis = "...") => {
	let trimmedString;

	// If string is too long, truncate
	if (string.length > length) {
		// Trim the string to the maximum length
		trimmedString = string.substr(0, length);

		// Re-trim if we are in the middle of a word and
		trimmedString = trimmedString.substr(0, Math.min(trimmedString.length, trimmedString.lastIndexOf(" "))) + elipsis;
		// Else, leave as is
	} else {
		trimmedString = string;
	}

	// Tidy the ending
	return trimmedString.replace(/^(.+)([\W_])(...)$/i, "$1$3");
};

/**
 * Checks if a string is valid JSON format
 *
 * @param {String} str
 */
export const isValidJSON = (str) => {
	try {
		JSON.parse(str);
	} catch (e) {
		return false;
	}
	return true;
};

/**
 * Returns a value using dot-notation key for recursive search
 * from the data object. If not found, the fallback value will
 * be returned.
 *
 * @param {Object} data
 * @param {String} key
 * @param {Any} fallback
 */
export const getValueFromObject = (data, key, fallback = null) => {
	var keyParts = key.split(".");

	// If single key (not iterative), return that value or fallback
	if (keyParts.length === 1) {
		return typeof data[keyParts[0]] !== "undefined" ? data[keyParts[0]] : fallback;
	} else {
		var firstKey = keyParts.splice(0, 1);
		return typeof data[firstKey] !== "undefined"
			? getValueFromObject(data[firstKey], keyParts.join("."), fallback)
			: fallback;
	}
};

/**
 * Removes weird known characters from text
 * @param text
 * @return {*}
 */
export const fixCharacters = (text) => {
	const search = ["Â"];
	const replace = [""];
	for (var i = 0; i < search.length; i++) {
		const regex = new RegExp(search[i], "gi");
		text = text.replace(regex, replace[i]);
	}

	return text;
};

/**
 * Get the offset of an element relative to document
 * @param el
 * @return {{top: number, left: number}}
 */
export const offsetOfElement = (el) => {
	if (el === null) {
		return {top: 0, left: 0};
	}
	var rect = el.getBoundingClientRect(),
		scrollLeft = window.pageXOffset || document.documentElement.scrollLeft,
		scrollTop = window.pageYOffset || document.documentElement.scrollTop;
	return {top: rect.top + scrollTop, left: rect.left + scrollLeft};
};

/**
 * An event handler that triggers on element resize
 * @param element
 * @param callback
 * @return {{deconstruct: deconstruct, element: *}}
 * @constructor
 */
export const ResizeSensor = (element, callback) => {
	const ro = new ResizeObserver(callback);
	ro.observe(element);

	return {
		element: element,
		deconstruct: () => {
			ro.unobserve(element);
		},
	};

};

/**
 * Returns a random hash
 * @param {Number} length
 * @returns {string}
 */
export const createRandomHash = (length = 32) => {
	let result = "";
	const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
	const charactersLength = characters.length;
	for (let i = 0; i < length; i++) {
		result += characters.charAt(Math.floor(Math.random() * charactersLength));
	}
	return result;
};

/**
 * Javascript implementation of PHP's uc_words
 * @param str
 * @return {string}
 */
export const ucWords = (str) => {
	return (str + "").replace(/^(.)|\s+(.)/g, function ($1) {
		return $1.toUpperCase();
	});
};

/**
 * Return an integer between two values, optional to be non-inclusive
 * @param {int} min
 * @param {int} max
 * @param {boolean} inclusive
 * @return {number}
 */
export const randomNumber = (min, max, inclusive = true) => {
	min = Math.ceil(min);
	max = Math.floor(max);
	return inclusive ? Math.floor(Math.random() * (max - min + 1) + min) : Math.floor(Math.random() * (max - min) + min);
};

/**
 * Pluralises a phrase based on a number
 * @param {int} number
 * @param {string} text
 * @param {string} suffix
 * @return {string}
 */
export const pluralise = (number, text, suffix = "s") => {
	if (isNaN(number) || number === 1) return text;
	return `${text}${suffix}`;
};

/**
 * Delay for a number of milliseconds
 * @param {int} delay Number of milliseconds to sleep for
 */
export const sleep = async (delay) => {
	await new Promise((r) => setTimeout(r, delay));
};

/**
 * Determines if the device is a touch based device (i.e. mobile phone)
 * @return {boolean|number}
 */
export const isTouchDevice = () => "ontouchstart" in window || navigator.msMaxTouchPoints;

/**
 * Returns a URL (argument 1) with persistant query string keys specfied in argument 2, 3...
 *
 * @return {string}
 */
export const urlWithPersistance = function () {
	const args = Array.from(arguments);

	if (args.length === 0) {
		throw new Error("Expected at least one argument [url] to be given to urlWithPersistance.");
	}

	const queryInterpreter = new URLSearchParams(window.location.search);

	let persistParams = {};
	const url = args.shift();

	if (args.length) {
		for (let i = 0; i < args.length; i++) {
			const paramKey = args[i];
			if (queryInterpreter.has(paramKey)) {
				persistParams[paramKey] = queryInterpreter.get(paramKey);
			}
		}
	}

	const esc = encodeURIComponent;
	const query = Object.keys(persistParams)
	.map((k) => esc(k) + "=" + esc(persistParams[k]))
	.join("&");

	const joiner = url.match(/\?[a-zA-Z0-9]+=.+$/) ? "&" : "?";

	return query.length ? `${url}${joiner}${query}` : url;
};

/**
 * Returns a new array with an element added
 * @param {array} arr
 * @param {array|any} elementsToAdd
 * @return {array}
 */
export const addToArray = (arr, elementsToAdd) => {
	if (Array.isArray(elementsToAdd)) {
		return [...arr, ...elementsToAdd];
	}

	return [...arr, elementsToAdd];
};

/**
 * Removes a single or number of elements from an array
 * @param {array} arr
 * @param {array|any} elementsToDelete
 * @return {array}
 */
export const deleteFromArray = (arr, elementsToDelete) => {
	let deleteOccurred = false;
	const newArray = Array.from(arr);
	if (Array.isArray(elementsToDelete)) {
		elementsToDelete.forEach((elementToDelete) => {
			const foundIndex = newArray.findIndex((el) => {
				return objectIdenticals(el, elementToDelete);
			});
			if (foundIndex > -1) {
				deleteOccurred = true;
				newArray.splice(foundIndex, 1);
			}
		});
	} else {
		const foundIndex = newArray.findIndex((el) => {
			return objectIdenticals(el, elementsToDelete);
		});
		if (foundIndex > -1) {
			deleteOccurred = true;
			newArray.splice(foundIndex, 1);
		}
	}

	if (deleteOccurred) {
		return newArray;
	}

	// Could not find item, so will not delete
	console.warn("Could not delete selected element from array", elementsToDelete, arr);
	return arr;
};

/**
 * Perform a fake PAI request and resolve
 * @return {Promise<unknown>}
 */
export const resolveFakeRequest = async (min = 300, max = 2000) => {
	return new Promise((resolve) => {
		setTimeout(() => {
			resolve(true);
		}, randomNumber(min, max));
	});
};

/**
 * Perform a fake PAI request and reject
 * @return {Promise<unknown>}
 */
export const rejectFakeRequest = async (min = 300, max = 2000) => {
	return new Promise((resolve, reject) => {
		setTimeout(() => {
			reject(true);
		}, randomNumber(min, max));
	});
};

/**
 * Perform a fake PAI request and by chance reject or resolve
 * @return {Promise<unknown>}
 */
export const performFakeRequest = async (min = 300, max = 2000) => {
	return new Promise((resolve, reject) => {
		const randomTimer = randomNumber(min, max);
		setTimeout(() => {
			if (randomTimer % 2 === 0) {
				resolve(true);
			} else {
				reject("Failed by chance");
			}
		}, randomNumber(min, max));
	});
};

/**
 * Returns a loose match result for an object or string in an array of objects
 * @param {array<object>} haystack
 * @param {object|string} needle
 * @param {string|null} cmpAttr
 * @return {null|*}
 */
export const findInArrayLoose = (haystack, needle, cmpAttr = null) => {
	if (isPlainObject(needle)) {
		return find(haystack, needle) || null;
	}

	if ((isString(needle) || isNumber(needle)) && isString(cmpAttr)) {
		return find(haystack, [cmpAttr, needle]) || null;
	}

	return null;
};

/**
 * Returns a loose match set of results for an array of objects/strings within a given set of objects
 * @param {array<object>} haystack
 * @param {array<object>|array<string>} needle
 * @param {string|null} cmpAttr
 * @return {array}
 */
export const findMultipleInArrayLoose = (haystack, needle, cmpAttr = null) => {
	return needle.map((stick) => findInArrayLoose(haystack, stick, cmpAttr)).filter((stick) => stick !== null);
};

/**
 * Checks if the operating system is in dark mode
 * @return {boolean}
 */
export const isOSInDarkMode = () => window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;

/**
 * Return the first element or a fallback value
 * @param {array} array
 * @param {any} fallback
 * @return {any}
 */
export const getFirstOrElse = (array, fallback = null) => getOrElse(() => array[0], fallback);

/**
 * Return the last element or a fallback value
 * @param {array} array
 * @param {any} fallback
 * @return {any}
 */
export const getLastOrElse = (array, fallback = null) => getOrElse(() => array[array.length - 1], fallback);

/**
 * Return the nth element or a fallback value
 * @param {array} array
 * @param {int} index
 * @param {any} fallback
 * @return {any}
 */
export const getNthOrElse = (array, index, fallback = null) => getOrElse(() => array[index], fallback);

/**
 * Find the highest/greatest common divisor
 * @param {number} a
 * @param {number} b
 * @return {number}
 */
export const highestCommonDivisor = (a, b) => {
	if (!b) {
		return a;
	}

	return highestCommonDivisor(b, a % b);
};

/**
 * Returns the difference of two objects
 * @param {object} object
 * @param {object} base
 * @return {object}
 */
export const objectDiff = (object, base) => {
	const changes = (object, base) => {
		return transform(object, function (result, value, key) {
			if (!isEqual(value, base[key])) {
				result[key] = isObject(value) && isObject(base[key]) ? changes(value, base[key]) : value;
			}
		});
	};
	return changes(object, base);
};

/**
 * Returns a key with user ID in it.
 * @param key
 * @return {string}
 */
export const constructStorageKey = (key) => {
	return `sn_uid_${readyForStorage()}_${encode(key)}`;
};

/**
 * Return string of userId from #root element.
 * @return {string|null}
 */
export const readyForStorage = () => {
	return document.getElementById("root").getAttribute("data-uid");
};


/**
 * Returns true if in local development environment.
 * @type {boolean}
 */
export const isDev = process.env.REACT_APP_ENV === "local";

/**
 * Returns true if app is hosted on app.staynimble.co.uk
 * @type {boolean}
 */
export const isOnProdDomain = getOrElse(() => process.env.REACT_APP_CLIENT_URL.indexOf("app.staynimble.co.uk") > -1, false);
